/* global axios */
import ApiClient from './ApiClient';

class InternalChatAPI extends ApiClient {
  constructor() {
    super('internal_chat/rooms', { accountScoped: true });
  }

  getRooms() {
    return axios.get(this.url);
  }

  getMessages(roomId, params = {}) {
    return axios.get(`${this.url}/${roomId}/messages`, { params });
  }

  searchMessages(roomId, params = {}) {
    return axios.get(`${this.url}/${roomId}/search`, { params });
  }

  createMessage(roomId, message) {
    const url = `${this.url}/${roomId}/messages`;

    if (message instanceof FormData) {
      return axios.post(url, message);
    }

    if (message.attachments && message.attachments.length > 0) {
      const formData = new FormData();
      formData.append('message[message_type]', message.message_type || 'file');
      formData.append('message[content]', message.content || '');
      message.attachments.forEach(file => {
        formData.append('message[attachments][]', file);
      });
      return axios.post(url, formData);
    }

    return axios.post(url, { message });
  }

  markRead(roomId, userId) {
    return axios.patch(`${this.url}/${roomId}/participants/${userId}`);
  }

  updateParticipantState(roomId, userId, state, options = {}) {
    return axios.patch(`${this.url}/${roomId}/participants/${userId}`, {
      state,
      muted_until: options.muted_until,
    });
  }

  createRoom({ kind, name, dmUserId, memberIds }) {
    const payload = { room: { kind, name } };
    if (dmUserId) payload.dm_user_id = dmUserId;
    if (memberIds && memberIds.length) payload.member_ids = memberIds;
    return axios.post(this.url, payload);
  }

  addMembers(roomId, memberIds) {
    return axios.post(`${this.url}/${roomId}/members`, {
      member_ids: memberIds,
    });
  }

  updateRoom(roomId, { name, memberIds, metadata }) {
    const payload = { room: {} };
    if (name !== undefined) payload.room.name = name;
    if (metadata !== undefined) payload.room.metadata = metadata;
    if (memberIds) payload.member_ids = memberIds;
    return axios.put(`${this.url}/${roomId}`, payload);
  }

  deleteMessage(roomId, messageId) {
    return axios.delete(`${this.url}/${roomId}/messages/${messageId}`);
  }

  addReaction(roomId, messageId, emoji) {
    return axios.post(`${this.url}/${roomId}/messages/${messageId}/reaction`, {
      emoji,
    });
  }

  removeReaction(roomId, messageId, emoji) {
    return axios.delete(`${this.url}/${roomId}/messages/${messageId}/reaction`, {
      data: { emoji },
    });
  }

  startCall(roomId, payload = {}) {
    return axios.post(`${this.url}/${roomId}/call/start`, payload);
  }

  endCall(roomId) {
    return axios.post(`${this.url}/${roomId}/call/end`);
  }

  fetchCallToken(roomId, params = {}) {
    return axios.get(`${this.url}/${roomId}/call/token`, { params });
  }

  getPinnedMessages(roomId) {
    return axios.get(`${this.url}/${roomId}/message_pins`);
  }

  pinMessage(roomId, messageId) {
    return axios.post(`${this.url}/${roomId}/message_pins`, { message_id: messageId });
  }

  unpinMessage(roomId, messageId) {
    return axios.delete(`${this.url}/${roomId}/message_pins/${messageId}`);
  }

  sendTyping(roomId, status = 'on') {
    return axios.post(`${this.url}/${roomId}/typing`, { typing_status: status });
  }

  getAuditLogs(roomId, params = {}) {
    return axios.get(`${this.url}/${roomId}/audit_logs`, { params });
  }
}

export default new InternalChatAPI();
